import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { PageResponse } from '@models/api';
import {
  getInventoryRemainedSalesRank,
  type GetSalesInventoryRemainedResponse,
} from '@apis/summary';
import { getTimestampRangeRollingDaysKst } from '@utils/time';

const DEFAULT_STALE_TIME_MS = 60_000; // Inventory data does not require high-frequency refresh.

/**
 * Provides inventory coverage data for the dashboard.
 *
 * Time range policy:
 * - Last 7 days excluding today
 * - Calculated in KST
 * - Converted to UTC timestamps before being sent to the API
 *
 * This range is intentionally aligned with shipped volume
 * to evaluate stock coverage and replenishment risk.
 */
export const useSummaryInventoryStatus = () => {
  const queryOptions: UseQueryOptions<
    PageResponse<GetSalesInventoryRemainedResponse>,
    Error
  > = {
    /**
     * The query key explicitly includes the time preset
     * to make cache semantics clear and predictable.
     */
    queryKey: ['dashboardSummaryInventoryStatus', 'R7_EXCLUDE_TODAY'],

    queryFn: () => {
      const { from, to } = getTimestampRangeRollingDaysKst(7, true);
      return getInventoryRemainedSalesRank({ from, to });
    },

    staleTime: DEFAULT_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  };

  return useQuery(queryOptions);
};
